import {useCallback, useMemo} from 'react';
import {useStateDispatch, useUser} from '@deezer/next/contexts/main/hooks';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {setUser} from '@deezer/next/contexts/main/actions';
import type {UserSetting} from '@deezer/next/services/types/userSettings';
import type {FeatureLabel} from '@deezer/next/services/types/features';

export const useUserSetting = <
	F extends FeatureLabel | 'user',
	K extends keyof UserSetting<F>,
>(
	feature: F,
	key: K,
): {
	setting: UserSetting<F>[K] | undefined;
	setSetting: (setting: UserSetting<F>[K] | undefined) => Promise<void>;
	deleteSetting: () => Promise<void>;
} => {
	const dispatch = useStateDispatch();
	const user = useUser();

	const setting = useMemo(() => {
		return user?.settings?.[feature]?.[key] || undefined;
	}, [feature, key, user?.settings]);

	const setSetting = useCallback(
		async (setting: UserSetting<F>[K] | undefined) => {
			if (!user) {
				return;
			}

			const newSettings = {
				...user.settings[feature],
				[key]: setting,
			};
			await StarmaniaApi.users.postSetting(feature, newSettings);

			const {data: userData} = await StarmaniaApi.users.getMe();
			dispatch(
				setUser({
					...user,
					settings: userData.settings,
				}),
			);
		},
		[dispatch, feature, key, user],
	);

	const deleteSetting = useCallback(() => setSetting(undefined), [setSetting]);

	return {setting, setSetting, deleteSetting};
};

export default useUserSetting;
